import { supabase } from "@/integrations/supabase/client";
import { normalizeMpn } from "@/lib/part-identity";
import type { MarketScanRecommendationSignal, RecommendationSignalKind } from "@/lib/market-scan-recommendations";
import type { SearchIntent } from "@/lib/search-intent";
import { offerDisplayGbpAmount, type SourcingOffer } from "@/lib/sourcing-offers";
export { buildSupplierRedirectPath } from "@/lib/supplier-redirect";

const ANONYMOUS_ID_KEY = "control-exchange:sourcing-anonymous-id:v1";
const ANALYTICS_SESSION_ID_KEY = "control-exchange:market-scan-session-id:v1";
const ANALYTICS_SEQUENCE_KEY = "control-exchange:market-scan-sequence:v1";
const ANALYTICS_ATTRIBUTION_KEY = "control-exchange:market-scan-attribution:v1";
const RECOMMENDATION_SIGNALS_KEY = "control-exchange:market-scan-signals:v1";
const MAX_LOCAL_RECOMMENDATION_SIGNALS = 60;
const LOCAL_SIGNAL_RETENTION_MS = 90 * 24 * 60 * 60 * 1000;
const pendingSearchRecords = new Map<string, Promise<boolean>>();
const completedSearchRecords = new Set<string>();
const failedSearchRecords = new Set<string>();
const pendingStartedSearchEvents = new Map<string, Promise<boolean>>();
const completedStartedSearchEvents = new Set<string>();
const pendingEventRecords = new Map<string, Promise<boolean>>();
const completedEventRecords = new Set<string>();
const outboundSearchProofs = new Map<string, string>();
const MAX_TRACKED_SEARCH_RECORDS = 50;
const MAX_TRACKED_EVENT_RECORDS = 500;

export type SourcingEventCategory = "acquisition" | "engagement" | "search" | "results" | "commercial" | "conversion" | "system";

type AnalyticsAttribution = {
  landingPath: string | null;
  referrerDomain: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
  utmTerm: string | null;
};

function safeSessionValue(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function setSafeSessionValue(key: string, value: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Strict privacy modes can disable session storage. Event recording still works.
  }
}

export function getMarketScanAnalyticsSessionId(): string {
  const existing = safeSessionValue(ANALYTICS_SESSION_ID_KEY);
  if (existing) return existing;
  const created = crypto.randomUUID();
  setSafeSessionValue(ANALYTICS_SESSION_ID_KEY, created);
  return created;
}

function nextAnalyticsSequence(): number {
  const current = Number.parseInt(safeSessionValue(ANALYTICS_SEQUENCE_KEY) || "0", 10);
  const next = Number.isFinite(current) && current >= 0 ? current + 1 : 1;
  setSafeSessionValue(ANALYTICS_SEQUENCE_KEY, String(next));
  return next;
}

function currentPagePath(): string | null {
  if (typeof window === "undefined") return null;
  const path = window.location.pathname || "/";
  return path.startsWith("/") ? path.slice(0, 500) : "/";
}

function readAttribution(): AnalyticsAttribution {
  if (typeof window === "undefined") {
    return { landingPath: null, referrerDomain: null, utmSource: null, utmMedium: null, utmCampaign: null, utmContent: null, utmTerm: null };
  }
  const stored = safeSessionValue(ANALYTICS_ATTRIBUTION_KEY);
  if (stored) {
    try {
      return JSON.parse(stored) as AnalyticsAttribution;
    } catch {
      // Replace malformed browser state with a fresh, bounded attribution snapshot.
    }
  }
  const params = new URLSearchParams(window.location.search);
  let referrerDomain: string | null = null;
  try {
    referrerDomain = document.referrer ? new URL(document.referrer).hostname.slice(0, 255) : null;
  } catch {
    referrerDomain = null;
  }
  const limited = (key: string, max: number) => params.get(key)?.trim().slice(0, max) || null;
  const attribution = {
    landingPath: currentPagePath(),
    referrerDomain,
    utmSource: limited("utm_source", 200),
    utmMedium: limited("utm_medium", 200),
    utmCampaign: limited("utm_campaign", 300),
    utmContent: limited("utm_content", 300),
    utmTerm: limited("utm_term", 300),
  };
  setSafeSessionValue(ANALYTICS_ATTRIBUTION_KEY, JSON.stringify(attribution));
  return attribution;
}

function deviceContext(): Record<string, unknown> {
  if (typeof window === "undefined") return {};
  const width = Math.max(0, Math.round(window.innerWidth || 0));
  return {
    analyticsSchemaVersion: 3,
    deviceCategory: width < 640 ? "mobile" : width < 1024 ? "tablet" : "desktop",
    viewportWidth: width,
    viewportHeight: Math.max(0, Math.round(window.innerHeight || 0)),
    language: navigator.language?.slice(0, 20) || null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone?.slice(0, 100) || null,
  };
}

function inferEventCategory(eventName: string): SourcingEventCategory {
  if (eventName === "market_scan_viewed") return "acquisition";
  if (eventName.startsWith("search_")) return "search";
  if (eventName === "results_rendered" || eventName === "alternative_clicked") return "results";
  if (eventName === "offer_click" || eventName === "filter_used" || eventName === "ranking_tab_used") return "commercial";
  if (eventName.includes("completed") || eventName.includes("accepted") || eventName === "rfq_created") return "conversion";
  if (eventName.startsWith("market_scan_") || eventName.startsWith("market_explorer_") || eventName.includes("recommendation") || eventName === "explore_more_opened") return "engagement";
  return "system";
}

export interface SearchLearningSource {
  /** Stable raw supplier identifier used to prevent multiple `web` sources overwriting each other. */
  sourceId?: string;
  sourceKey: string;
  status: "loading" | "error" | "empty" | "success";
  resultCount: number;
  failureCode?: string | null;
  fromCache?: boolean;
}

export function summarizeIdentityMatches(matches: Array<{
  matchType: "exact" | "variant" | "fuzzy";
  source?: string | null;
}>): {
  identityOutcome: "exact" | "variant" | "fuzzy" | "none";
  topResultMatchType: "exact" | "variant" | "fuzzy" | null;
  topResultSource: string | null;
} {
  const topMatch = matches.find((match) => match.matchType === "exact")
    ?? matches.find((match) => match.matchType === "variant")
    ?? matches[0];
  return {
    identityOutcome: topMatch?.matchType ?? "none",
    topResultMatchType: topMatch?.matchType ?? null,
    topResultSource: topMatch?.source ?? null,
  };
}

/**
 * Produce low-cardinality, privacy-conscious signals that can be joined to a
 * later supplier click. Raw queries remain in the parent search record; this
 * snapshot deliberately avoids page HTML, error messages and personal data.
 */
export function buildSearchLearningProperties(input: {
  intent: SearchIntent | null;
  offers: SourcingOffer[];
  sources: SearchLearningSource[];
}): Record<string, unknown> {
  const identityCounts = { exact: 0, variant: 0, fuzzy: 0 };
  const scanConfidenceCounts = { high: 0, medium: 0, review: 0 };
  let pricedOfferCount = 0;
  let completePriceCount = 0;

  for (const offer of input.offers) {
    identityCounts[offer.matchType] += 1;
    scanConfidenceCounts[offer.scanConfidence] += 1;
    if (offerDisplayGbpAmount(offer) != null) pricedOfferCount += 1;
    if (offer.costIsComplete) completePriceCount += 1;
  }

  return {
    learningSchemaVersion: 2,
    searchMode: input.intent?.mode ?? "unknown",
    interpretationProvider: input.intent?.provider ?? "unknown",
    interpretationConfidence: input.intent?.confidence ?? null,
    recognizedManufacturer: input.intent?.manufacturer ?? null,
    marketExplorerCategory: input.intent?.category ?? null,
    productFamily: input.intent?.productFamily ?? null,
    partNumberDetected: Boolean(input.intent?.partNumber),
    specificationNames: [...new Set(input.intent?.specifications.map((specification) => specification.name) ?? [])],
    keywordCount: input.intent?.keywords.length ?? 0,
    identityCounts,
    scanConfidenceCounts,
    pricedOfferCount,
    completePriceCount,
    topScanConfidenceScore: input.offers.reduce(
      (highest, offer) => Math.max(highest, offer.scanConfidenceScore),
      0,
    ),
    sourceOutcomes: Object.fromEntries(input.sources.map((source) => [source.sourceId ?? source.sourceKey, {
      sourceKey: source.sourceKey,
      status: source.status,
      resultCount: source.resultCount,
      failureCode: source.failureCode ?? null,
      fromCache: Boolean(source.fromCache),
    }])),
  };
}

async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function getSourcingSearchProof(searchId: string | null | undefined): string | null {
  return searchId ? outboundSearchProofs.get(searchId) ?? null : null;
}

function reportAnalyticsFailure(operation: string, error: unknown): void {
  if (typeof console !== "undefined") {
    console.warn(`[sourcing analytics] ${operation} was not persisted`, error);
  }
}

function isUniqueViolation(error: unknown): boolean {
  return Boolean(error && typeof error === "object" && "code" in error && error.code === "23505");
}

function rememberCompletedEvent(clientEventId: string): void {
  completedEventRecords.add(clientEventId);
  while (completedEventRecords.size > MAX_TRACKED_EVENT_RECORDS) {
    const oldest = completedEventRecords.values().next().value;
    if (!oldest) break;
    completedEventRecords.delete(oldest);
  }
}

const OPTIONAL_ANALYTICS_COLUMNS = [
  "analytics_session_id",
  "landing_path",
  "referrer_domain",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "event_category",
  "page_path",
  "client_occurred_at",
  "sequence_number",
];

function isMissingAnalyticsColumn(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const code = "code" in error ? String(error.code) : "";
  if (code !== "42703" && code !== "PGRST204") return false;
  const description = JSON.stringify(error).toLowerCase();
  return OPTIONAL_ANALYTICS_COLUMNS.some((column) => description.includes(column));
}

export function getSourcingAnonymousId(): string {
  if (typeof window === "undefined") return "server";
  const existing = localStorage.getItem(ANONYMOUS_ID_KEY);
  if (existing) return existing;
  const created = crypto.randomUUID();
  localStorage.setItem(ANONYMOUS_ID_KEY, created);
  return created;
}

function recommendationSignalKind(eventName: string): RecommendationSignalKind | null {
  if (eventName === "recommendation_clicked") return "recommendation_click";
  if (eventName === "supplier_outbound_click" || eventName === "offer_click") return "supplier_click";
  if (eventName === "search_completed") return "search";
  return null;
}

function storeLocalRecommendationSignal(signal: MarketScanRecommendationSignal): void {
  if (typeof window === "undefined") return;
  try {
    const existing = getLocalRecommendationSignals();
    const next = [signal, ...existing]
      .filter((item) => new Date(item.occurredAt).getTime() >= Date.now() - LOCAL_SIGNAL_RETENTION_MS)
      .slice(0, MAX_LOCAL_RECOMMENDATION_SIGNALS);
    localStorage.setItem(RECOMMENDATION_SIGNALS_KEY, JSON.stringify(next));
  } catch {
    // Browsers may deny storage in strict privacy modes; analytics remains best-effort.
  }
}

export function getLocalRecommendationSignals(): MarketScanRecommendationSignal[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(RECOMMENDATION_SIGNALS_KEY) || "[]") as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((item): MarketScanRecommendationSignal[] => {
      if (!item || typeof item !== "object") return [];
      const value = item as Record<string, unknown>;
      if (!(["search", "supplier_click", "recommendation_click"] as const).includes(value.kind as RecommendationSignalKind)) return [];
      if (typeof value.occurredAt !== "string" || !Number.isFinite(new Date(value.occurredAt).getTime())) return [];
      return [{
        kind: value.kind as RecommendationSignalKind,
        audience: "personal",
        normalizedMpn: typeof value.normalizedMpn === "string" ? value.normalizedMpn : null,
        manufacturer: typeof value.manufacturer === "string" ? value.manufacturer : null,
        sourceKey: typeof value.sourceKey === "string" ? value.sourceKey : null,
        occurredAt: value.occurredAt,
      }];
    });
  } catch {
    return [];
  }
}

export async function recordSourcingEvent(input: {
  eventName: string;
  eventCategory?: SourcingEventCategory;
  /** Stable key for lifecycle events that must be exactly-once across retries and remounts. */
  idempotencyKey?: string;
  searchId?: string | null;
  userId?: string | null;
  sourceKey?: string | null;
  offerId?: string | null;
  properties?: Record<string, unknown>;
}): Promise<boolean> {
  const clientEventId = input.idempotencyKey || crypto.randomUUID();
  if (input.idempotencyKey) {
    if (completedEventRecords.has(clientEventId)) return true;
    const pending = pendingEventRecords.get(clientEventId);
    if (pending) return pending;
  }
  if (input.eventName === "market_scan_viewed") readAttribution();
  const localSignalKind = recommendationSignalKind(input.eventName);
  if (localSignalKind) {
    const normalizedMpnValue = input.properties?.normalizedMpn ?? input.properties?.partNumber;
    storeLocalRecommendationSignal({
      kind: localSignalKind,
      audience: "personal",
      normalizedMpn: typeof normalizedMpnValue === "string" ? normalizeMpn(normalizedMpnValue) : null,
      manufacturer: typeof input.properties?.recognizedManufacturer === "string"
        ? input.properties.recognizedManufacturer
        : typeof input.properties?.manufacturer === "string"
          ? input.properties.manufacturer
          : null,
      sourceKey: input.sourceKey ?? null,
      occurredAt: new Date().toISOString(),
    });
  }
  const operation = (async () => {
    try {
      const creationSucceeded = input.searchId
        ? await (pendingSearchRecords.get(input.searchId) ?? Promise.resolve(!failedSearchRecords.has(input.searchId)))
        : true;
      const persistedSearchId = creationSucceeded ? input.searchId ?? null : null;
      const legacyRow = {
        client_event_id: clientEventId,
        search_id: persistedSearchId,
        user_id: input.userId ?? null,
        anonymous_id: getSourcingAnonymousId(),
        event_name: input.eventName,
        source_key: input.sourceKey ?? null,
        offer_id: input.offerId ?? null,
        properties: creationSucceeded
          ? { ...deviceContext(), ...(input.properties ?? {}) }
          : { ...deviceContext(), ...(input.properties ?? {}), intendedSearchId: input.searchId },
      };
      const row = {
        ...legacyRow,
        analytics_session_id: getMarketScanAnalyticsSessionId(),
        event_category: input.eventCategory ?? inferEventCategory(input.eventName),
        page_path: currentPagePath(),
        client_occurred_at: new Date().toISOString(),
        sequence_number: nextAnalyticsSequence(),
      };
      let lastError: unknown = null;
      for (const delayMs of [0, 250, 1_000]) {
        if (delayMs) await new Promise((resolve) => setTimeout(resolve, delayMs));
        try {
          let { error } = await (supabase as any).from("sourcing_events").insert(row);
          if (isMissingAnalyticsColumn(error)) {
            ({ error } = await (supabase as any).from("sourcing_events").insert(legacyRow));
          }
          // Anonymous analytics rows are intentionally not selectable under RLS,
          // so PostgREST upserts cannot be used for idempotency. A retry that hits
          // the unique client_event_id is equivalent to a successful first write.
          if (!error || isUniqueViolation(error)) return true;
          lastError = error;
        } catch (error) {
          lastError = error;
        }
      }
      reportAnalyticsFailure(`event ${input.eventName}`, lastError);
      return false;
    } catch (error) {
      reportAnalyticsFailure(`event ${input.eventName}`, error);
      return false;
    }
  })();
  if (!input.idempotencyKey) return operation;
  const trackedOperation = operation.then((succeeded) => {
    if (succeeded) rememberCompletedEvent(clientEventId);
    return succeeded;
  }).finally(() => {
    pendingEventRecords.delete(clientEventId);
  });
  pendingEventRecords.set(clientEventId, trackedOperation);
  return trackedOperation;
}

export interface SubmittedSourcingSearch {
  id: string;
  userId?: string | null;
  query: string;
  normalizedQuery: string;
  quantity: number;
  deliveryCountry: string;
  displayCurrency?: "GBP" | "EUR";
  requiredBy?: string | null;
  acceptedConditions: string[];
  machineDown: boolean;
}

/**
 * Create the parent search row before any child events are recorded. The
 * promise is also registered locally so impression/click events wait for the
 * foreign-key target even when results render very quickly.
 */
export function recordSubmittedSearch(input: SubmittedSourcingSearch): Promise<boolean> {
  const pending = pendingSearchRecords.get(input.id);
  if (pending) return pending;
  if (completedSearchRecords.has(input.id)) return Promise.resolve(true);
  storeLocalRecommendationSignal({
    kind: "search",
    audience: "personal",
    normalizedMpn: normalizeMpn(input.normalizedQuery || input.query),
    occurredAt: new Date().toISOString(),
  });
  // Keep the same proof across a retry. If the first HTTP response was lost
  // after the row committed, an idempotent retry must still match that row.
  const outboundProof = outboundSearchProofs.get(input.id) ?? crypto.randomUUID();
  outboundSearchProofs.set(input.id, outboundProof);
  if (outboundSearchProofs.size > MAX_TRACKED_SEARCH_RECORDS) {
    const oldestSearchId = outboundSearchProofs.keys().next().value;
    if (oldestSearchId) {
      outboundSearchProofs.delete(oldestSearchId);
      completedSearchRecords.delete(oldestSearchId);
      failedSearchRecords.delete(oldestSearchId);
      pendingStartedSearchEvents.delete(oldestSearchId);
      completedStartedSearchEvents.delete(oldestSearchId);
    }
  }
  const operation = (async () => {
    try {
      const outboundProofHash = await sha256Hex(outboundProof);
      const attribution = readAttribution();
      const legacyRow = {
        id: input.id,
        user_id: input.userId ?? null,
        anonymous_id: getSourcingAnonymousId(),
        query: input.query,
        normalized_query: input.normalizedQuery,
        quantity: input.quantity,
        delivery_country: input.deliveryCountry || "United Kingdom",
        display_currency: input.displayCurrency || "GBP",
        outbound_proof_hash: outboundProofHash,
        required_by: input.requiredBy || null,
        accepted_conditions: input.acceptedConditions,
        machine_down: input.machineDown,
      };
      const row = {
        ...legacyRow,
        analytics_session_id: getMarketScanAnalyticsSessionId(),
        landing_path: attribution.landingPath,
        referrer_domain: attribution.referrerDomain,
        utm_source: attribution.utmSource,
        utm_medium: attribution.utmMedium,
        utm_campaign: attribution.utmCampaign,
        utm_content: attribution.utmContent,
        utm_term: attribution.utmTerm,
      };
      let { error } = await (supabase as any).from("sourcing_searches").insert(row);
      if (isMissingAnalyticsColumn(error)) {
        ({ error } = await (supabase as any).from("sourcing_searches").insert(legacyRow));
      }
      // The primary key makes a lost-response retry safe without granting
      // anonymous SELECT/UPDATE access merely to support an upsert.
      if (error && !isUniqueViolation(error)) {
        failedSearchRecords.add(input.id);
        reportAnalyticsFailure("search submission", error);
        return false;
      }
      completedSearchRecords.add(input.id);
      failedSearchRecords.delete(input.id);
      return true;
    } catch (error) {
      failedSearchRecords.add(input.id);
      reportAnalyticsFailure("search submission", error);
      return false;
    }
  })();
  pendingSearchRecords.set(input.id, operation);
  void operation.finally(() => {
    if (pendingSearchRecords.get(input.id) === operation) pendingSearchRecords.delete(input.id);
  });
  return operation;
}

/**
 * Persist the parent search and its lifecycle-start event as one ordered,
 * idempotent operation. Concurrent React effects share the same operation,
 * while `onParentReady` lets outbound links become usable as soon as their
 * foreign-key target exists rather than waiting for best-effort analytics.
 */
export async function ensureSourcingSearchStarted(
  input: SubmittedSourcingSearch,
  properties: Record<string, unknown>,
  onParentReady?: () => void,
): Promise<boolean> {
  if (completedStartedSearchEvents.has(input.id)) {
    onParentReady?.();
    return true;
  }

  const pending = pendingStartedSearchEvents.get(input.id);
  if (pending) {
    const completed = await pending;
    if (completedSearchRecords.has(input.id)) onParentReady?.();
    return completed;
  }

  const operation = (async () => {
    let parentReady = false;
    for (const delayMs of [0, 400, 1_200]) {
      if (delayMs) await new Promise((resolve) => setTimeout(resolve, delayMs));
      if (await recordSubmittedSearch(input)) {
        parentReady = true;
        break;
      }
    }
    if (!parentReady) return false;

    onParentReady?.();
    const recorded = await recordSourcingEvent({
      eventName: "search_started",
      idempotencyKey: `${input.id}:search_started`,
      searchId: input.id,
      userId: input.userId,
      properties,
    });
    if (recorded) completedStartedSearchEvents.add(input.id);
    return recorded;
  })();

  pendingStartedSearchEvents.set(input.id, operation);
  try {
    return await operation;
  } finally {
    if (pendingStartedSearchEvents.get(input.id) === operation) {
      pendingStartedSearchEvents.delete(input.id);
    }
  }
}

export async function recordCompletedSearch(input: SubmittedSourcingSearch & {
  identityOutcome: "exact" | "variant" | "fuzzy" | "none";
  topResultMatchType?: "exact" | "variant" | "fuzzy" | null;
  topResultSource?: string | null;
  resultCounts: Record<string, number>;
}): Promise<boolean> {
  try {
    const creationSucceeded = await (pendingSearchRecords.get(input.id) ?? Promise.resolve(!failedSearchRecords.has(input.id)));
    if (!creationSucceeded) return false;
    const { data, error } = await (supabase as any).rpc("complete_sourcing_search", {
      p_search_id: input.id,
      p_anonymous_id: getSourcingAnonymousId(),
      p_quantity: input.quantity,
      p_delivery_country: input.deliveryCountry || "United Kingdom",
      p_display_currency: input.displayCurrency || "GBP",
      p_required_by: input.requiredBy || null,
      p_accepted_conditions: input.acceptedConditions,
      p_machine_down: input.machineDown,
      p_identity_outcome: input.identityOutcome,
      p_top_result_match_type: input.topResultMatchType ?? null,
      p_top_result_source: input.topResultSource ?? null,
      p_result_counts: input.resultCounts,
    });
    if (error) {
      reportAnalyticsFailure("search completion", error);
      return false;
    }
    if (data !== true) {
      reportAnalyticsFailure("search completion", new Error("Search record ownership check failed"));
      return false;
    }
    return true;
  } catch (error) {
    reportAnalyticsFailure("search completion", error);
    return false;
  }
}
